/**
 * "This key might be exposed to Organization owner and N others" — the key
 * detail's line, the delete confirm and the leave-org notice all read it from
 * here (model C: roles — the owner first —, teams, people shared with directly or
 * who opened it, apps, outside orgs).
 */
import { useTranslation } from '@papan/i18n'
import type { KeybookExposure } from '../client/api.js'
import { exposureEntities, exposureEntityName } from './exposure-view.js'

/** The headline's parts, or null when nobody is on the record. */
export function exposureHeadlineParts(data: KeybookExposure) {
  const entities = exposureEntities(data)
  if (entities.length === 0) return null
  // The owner's own reader row (listed in the dialog for its "Opened" date) is
  // the same person as the first entity — not one of the "others". Only when the
  // owner ROLE entry is there to stand for them (a key not yet backfilled can
  // hold reader rows alone).
  const ownerRole = entities.some(e => e.kind === 'role' && e.item.id === 'owner')
  const others = (ownerRole
    ? entities.filter(e => !(e.kind === 'email' && e.item.isOrganizationOwner))
    : entities).length - 1
  // The suffix counts ENTITIES, like the headline (a team or role that no longer
  // opens the key counts as one). D29: only Manage's (full) answer carries
  // current access — the suffix is not shown to anyone else.
  const noLonger = data.view === 'full' ? entities.filter(e => e.item.stillHasAccess === false).length : 0
  return { first: entities[0]!, others, noLonger }
}

export function ExposureHeadline({ data, className = 'text-xs themed-text leading-snug' }: { data: KeybookExposure; className?: string }) {
  const { t } = useTranslation('keybook')
  const parts = exposureHeadlineParts(data)
  if (!parts) return null
  return (
    <p className={className} data-testid="keybook-exposure-headline">
      {t('exposure.headline')}{' '}
      <strong className="font-semibold">{exposureEntityName(t, parts.first)}</strong>
      {parts.others > 0 && <>{' '}{t('exposure.headlineAnd')}{' '}<strong className="font-semibold">{t('exposure.headlineOthers', { count: parts.others })}</strong></>}
      {parts.noLonger > 0 && (
        <span className="themed-text-muted"> · {t('exposure.noLonger', { count: parts.noLonger })}</span>
      )}
    </p>
  )
}
